import {componentInit} from "../tools/CommonReact";
import {checkRoleAdmin} from "./CommonUIData";

const rumState = {
    rum: null,
    oUser: null,
    pathParams: null,
};

export function checkRum({vals, ftrFlags}) {
    const ffrum = ftrFlags?.ff_rum;
    if (ftrFlags && componentInit(vals, Object.values(ffrum ?? {}), "_init_rum")) {
        const wasEnabled = sessionStorage.getItem("rumEnabled") === "true";
        if (!wasEnabled !== !ffrum?.uiEnabled) {
            sessionStorage.setItem("rumEnabled", !!ffrum?.uiEnabled);
            if (!wasEnabled)
                initRum();
            else
                stopRum();
        }
    }
}

export function initRum() {
    if (process.env.NODE_ENV === "development") return;
    if (sessionStorage.getItem("rumEnabled") !== "true") return;

    impl?.init(() => {
        setRumUser();                               // checks if setRumUser has been called before init, and applies user.
        setRumPage();
    });
}

export function setRumUser({oUser} = {}) {
    oUser ??= rumState.oUser;
    rumState.oUser = oUser;
    oUser && impl?.setRumUser(oUser);
}

export function setRumPage(path, params) {
    if (!path) ({path, params} = rumState.pathParams ?? {});
    else rumState.pathParams = {path, params};
    impl?.setRumPage(path, params);
}

function stopRum() {
    console.log("RUM hushed");
    impl?.stopRum();
    rumState.rum = null;
}


const rumImpl = {
    DataDog: {
        init: afterInit => {
            import(window).then(({datadogRum}) => {       // import '@datadog/browser-rum'
                rumState.rum = datadogRum;                                  // DataDog
                datadogRum.init({
                    applicationId: "efedb472-493a-414c-9567-7ac62683a41d",  // Your RUM Application ID from Datadog
                    clientToken: "pub0aebf4f6e1504a3d2c6a3c15ff7a0036",     // Your public Client Token from Datadog
                    site: "datadoghq.com",                                  // Datadog site
                    service: "gytpol-ui",                                   // Logical service name (e.g., "frontend-app", "customer-portal")
                    env: window.location?.hostname?.split(".")[1],          // Environment tag: "dev", or "eu" or "us".
                    version: process.env.REACT_APP_VER_UI,                  // (Optional) App version – helps track errors by version
                    sampleRate: 100,                                        // % of sessions to collect (0–100). 100 = capture all.
                    sessionReplaySampleRate: 100,                           // % of sessions to record video (0–100). Lower this for performance.
                    trackInteractions: true,                                // Enables auto-tracking of user interactions (clicks, inputs, etc.)
                    defaultPrivacyLevel: "allow",                           // Options: - "mask-user-input": masks all input values by default - "allow": allows input values to be captured - "mask": masks all text content including inputs
                    silentMultipleInit: true,                               // (Optional) Prevents crash if init is called more than once
                    trackViewsManually: true,
                    beforeSend: impl.beforeSend,                            // filter outgoing events
                });
                afterInit();
            });
        },
        setUser: oUser => rumState.rum?.setUser({
            id: oUser.user,
            email: oUser.user,
            username: oUser.username,
        }),
        setRumPage: (path, params) =>
            rumState.rum?.startView({name: path, context: params}),
        beforeSend: (event, context) => !!rumState.rum,
        stopRum: () => {
            rumState.rum?.stopSessionReplayRecording();
            rumState.rum?.stopSession();
        }
    },

    Grafana: {
        init: afterInit => {
            import(window).then(({getWebInstrumentations, initializeFaro}) => {             // "@grafana/faro-web-sdk"
                import(window).then(({TracingInstrumentation}) => {                         // "@grafana/faro-web-tracing"
                    rumState.rum = initializeFaro({
                        url: 'https://faro-collector-prod-us-east-2.grafana.net/collect/c3165252334b80478ba9c176faa6061c',
                        isolate: true, //
                        app: {
                            name: 'remedio-ui',
                            version: process.env.REACT_APP_VER_UI,
                            environment: window.location?.hostname?.split(".")[1],          // Environment tag: "dev", or "eu" or "us".
                        },
                        sessionTracking: {
                            enabled: true,
                            session: {
                                attributes: {
                                    tenantId: window.location?.hostname?.split('.')[0],
                                    customerName: window.__CUSTOMER_NAME__ || "",
                                },
                            },
                        },
                        instrumentations: [
                            ...getWebInstrumentations(),        // Mandatory, omits default instrumentations otherwise.
                            new TracingInstrumentation(),       // Tracing package to get end-to-end visibility for HTTP requests.
                        ],
                        experimental: {
                            trackNavigation: true,
                        },
                    });
                    afterInit();
                });
            });
        },
        setRumUser: oUser => rumState.rum?.api.setUser({
            id: oUser.user,
            email: oUser.user,
            username: oUser.username,
            roles: checkRoleAdmin(oUser.perms?.[0])? "Admin" : "User",
        }),
        setRumPage: (path, params) => rumState.rum?.api.setPage({
            url: path,              // or a synthetic URL if you do not change the real one
            id: path,               // nice short id to filter on in Grafana
            attributes: params,
        }),
        stopRum: () => {
            rumState.rum?.api.resetUser();
            rumState.rum?.api.setApp?.({name: 'my-app', version: '1.0.0'});         // clear meta that might identify tenant or user
        }
    },

    Coralogix: {
        init: afterInit => {
            import("@coralogix/browser").then(({CoralogixRum}) => {             // "@coralogix/browser"
                rumState.rum = CoralogixRum;
                rumState.rum.init({
                    public_key: "cxtp_4OVyDMajaIcEXHIUvHwGFk7X1bNfZK",
                    application: "gytpol-ui",
                    version: process.env.REACT_APP_VER_UI,
                    coralogixDomain: "US1",
                    sessionRecordingConfig: {
                        enable: true,
                        autoStartSessionRecording: true,    // Automatically records your session when SDK is up.
                        recordConsoleEvents: true,          // Will record all console events from dev tools. Levels: log, debug, warn, error, info, table etc..
                        sessionRecordingSampleRate: 100,    // Percentage of overall sessions recording being tracked, defaults to 100% and applied after the overall sessionSampleRate.
                    },
                    traceParentInHeader: {
                        enabled: true,
                        options: {
                            // if backend domain differs, configure CORS URLs:
                            propagateTraceHeaderCorsUrls: [new RegExp('.*')],
                        },
                    },
                });
                rumState.rum.setLabels({
                    tenant: window.location?.hostname?.split('.')[0],
                    env: window.location?.hostname?.split(".")[1],
                });
                afterInit();
            });
        },
        setRumUser: oUser => rumState.rum?.setUserContext({
            user_id: oUser.user,
            user_name: oUser.username,
            user_email: oUser.user,
            user_metadata: {
                role: checkRoleAdmin(oUser.perms?.[0])? "admin" : "user",
            },
        }),
        setRumPage: (path, params) => {
            rumState.rum && console.log("setRumPage", path, params);
        },
        stopRum: () => {
            rumState.rum?.stopSessionRecording?.();
            rumState.rum?.shutdown?.();
        },
    }
};

const impl = rumImpl.Coralogix;
